import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useAuth } from "../../auth/authStore";
import { useToast } from "../../ui/toast/ToastProvider";
import Modal from "../ui/Modal";
import { sessionsApi } from "../../api/sessionsApi";
import { dictApi } from "../../api/dictApi";
import { filesApi } from "../../api/filesApi";
import { useSubscriptionLimitsGate } from "../../subscription/useSubscriptionLimitsGate";

import { resolveUrl } from "../../utils/urls";
import { assignExternalUrl } from "../../utils/safeUrl";
import { API_BASE_URL } from "../../config/env";
import GroupSessionDetailPage from "./GroupSessionDetailPage";

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toStoredFilePath(u) {
    if (!u) return "";
    try {
        const api = new URL(API_BASE_URL);
        const nu = new URL(u, api);
        if (nu.origin === api.origin) return `${nu.pathname}${nu.search || ""}`;
        return u;
    } catch { return u; }
}

function pad2(n) { return String(n).padStart(2, "0"); }

function fmtDateTimeRange(startIso, endIso) {
    try {
        const s = startIso ? new Date(startIso) : null;
        const e = endIso ? new Date(endIso) : null;
        if (!s || Number.isNaN(s.getTime())) return "—";
        const dd = `${pad2(s.getDate())}.${pad2(s.getMonth() + 1)}.${s.getFullYear()}`;
        const st = `${pad2(s.getHours())}:${pad2(s.getMinutes())}`;
        let et = "";
        if (e && !Number.isNaN(e.getTime())) et = `${pad2(e.getHours())}:${pad2(e.getMinutes())}`;
        return et ? `${dd} • ${st}–${et}` : `${dd} • ${st}`;
    } catch { return "—"; }
}

// ISO → { date: "YYYY-MM-DD", time: "HH:MM" }
function isoToDateAndTime(iso) {
    if (!iso) return { date: "", time: "" };
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return { date: "", time: "" };
    return {
        date: `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`,
        time: `${pad2(d.getHours())}:${pad2(d.getMinutes())}`,
    };
}

// "YYYY-MM-DD" + "HH:MM" → ISO string
function dateAndTimeToIso(date, time) {
    if (!date || !time) return null;
    const d = new Date(`${date}T${time}:00`);
    if (Number.isNaN(d.getTime())) return null;
    return d.toISOString();
}

// Маска времени: цифры → "HH:MM"
function maskTime(raw) {
    const digits = raw.replace(/\D/g, "").slice(0, 4);
    if (digits.length <= 2) return digits;
    return digits.slice(0, 2) + ":" + digits.slice(2);
}

function isValidTime(v) {
    if (!v) return true;
    const m = v.match(/^(\d{2}):(\d{2})$/);
    if (!m) return false;
    return Number(m[1]) <= 23 && Number(m[2]) <= 59;
}

function toCode(x) {
    if (x == null) return "";
    if (typeof x === "string" || typeof x === "number") return String(x);
    if (typeof x === "object") return String(x.code || x.title || x.name || "");
    return String(x);
}

function humanStatus(statusRaw) {
    const s = toCode(statusRaw);
    switch (s) {
        case "OPEN": return "Открыта";
        case "PENDING_PAYMENT": return "Ожидает оплату";
        case "PAID": return "Оплачено";
        case "CANCELLED": return "Закрыта";
        case "COMPLETED": return "Завершена";
        case "NO_SHOW": return "Неявка";
        default: return s || "—";
    }
}

function statusKind(statusRaw) {
    const s = toCode(statusRaw);
    if (s === "OPEN") return "open";
    if (s === "PENDING_PAYMENT") return "pending";
    if (s === "PAID") return "paid";
    if (s === "CANCELLED") return "cancelled";
    if (s === "COMPLETED") return "completed";
    if (s === "NO_SHOW") return "noshow";
    return "default";
}

function pickListResp(r) {
    if (Array.isArray(r)) return { items: r, page: 0, size: r.length, hasMore: false };
    const items = r?.content || r?.items || r?.data || [];
    const page = r?.number ?? r?.page ?? 0;
    const size = r?.size ?? r?.pageSize ?? 20;
    const total = r?.totalElements ?? r?.total ?? null;
    const hasMore = total != null ? (page + 1) * size < total : Boolean(r?.hasNext ?? r?.has_more);
    return { items: Array.isArray(items) ? items : [], page, size, total, hasMore };
}

function emptyDraft() {
    return {
        id: null,
        title: "",
        coverImg: "",       // итоговый путь/URL для бэка
        cover: "",
        coverMode: "file",  // "file" | "url"
        coverUrl: "",       // поле ввода URL
        sessionFormat: "ONLINE",
        description: "",
        date: "",
        timeStart: "",
        timeEnd: "",
        priceAtTime: "",
        capacityClients: "",
        minCapacity: "",
        allowJoin: true,
        themeIds: [],
        methodIds: [],
    };
}

function participantLabel(p) {
    const name = p?.name || p?.fullName || "";
    const email = p?.email || "";
    if (name && email) return `${name} · ${email}`;
    return name || email || "—";
}

// ─── Иконки раздела: без emoji, в стиле проекта ─────────────────────────────
function GsIcon({ name, className = "", size = 18 }) {
    const props = {
        className: `gs-icon ${className}`.trim(),
        width: size,
        height: size,
        viewBox: "0 0 24 24",
        fill: "none",
        xmlns: "http://www.w3.org/2000/svg",
        "aria-hidden": "true",
        focusable: "false",
    };
    const line = { stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round" };

    switch (name) {
        case "users":
            return <svg {...props}><path {...line} d="M8.5 11a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z" /><path {...line} d="M3.8 19.3c.7-3 2.4-5 4.7-5s4 2 4.7 5" /><path {...line} d="M16.8 10.6a2.8 2.8 0 1 0-.6-5.5" /><path {...line} d="M15 14.2c2.4.2 4.1 2.2 4.8 5.1" /></svg>;
        case "shield":
            return <svg {...props}><path {...line} d="M12 3.3 5.7 5.8v5.1c0 4.3 2.6 7.8 6.3 9.8 3.7-2 6.3-5.5 6.3-9.8V5.8L12 3.3Z" /><path {...line} d="m9.4 12 1.8 1.8 3.7-4" /></svg>;
        case "folder":
            return <svg {...props}><path {...line} d="M3.8 6.4h6l1.8 2.1h8.6v8.7a2 2 0 0 1-2 2H5.8a2 2 0 0 1-2-2V6.4Z" /><path {...line} d="M3.8 8.5h16.4" /></svg>;
        case "calendar":
            return <svg {...props}><rect {...line} x="4" y="5" width="16" height="15" rx="3" /><path {...line} d="M4 9h16M8 3.5v3M16 3.5v3" /></svg>;
        case "monitor":
            return <svg {...props}><rect {...line} x="4" y="5" width="16" height="11" rx="2.5" /><path {...line} d="M9 20h6M12 16v4" /></svg>;
        case "pin":
            return <svg {...props}><path {...line} d="M12 21s6-5.2 6-11a6 6 0 0 0-12 0c0 5.8 6 11 6 11Z" /><circle {...line} cx="12" cy="10" r="2.3" /></svg>;
        case "tag":
            return <svg {...props}><path {...line} d="M20 12.4 12.4 20a2.1 2.1 0 0 1-3 0L4 14.6V4h10.6L20 9.4a2.1 2.1 0 0 1 0 3Z" /><circle cx="8" cy="8" r="1.2" fill="currentColor" /></svg>;
        case "pulse":
            return <svg {...props}><path {...line} d="M3 12h4l2.2-6 4 12 2.2-6H21" /></svg>;
        case "wifi":
            return <svg {...props}><path {...line} d="M5.2 10a10.5 10.5 0 0 1 13.6 0" /><path {...line} d="M8.2 13a6 6 0 0 1 7.6 0" /><path {...line} d="M11 16a1.7 1.7 0 0 1 2 0" /><circle cx="12" cy="18.2" r=".7" fill="currentColor" /></svg>;
        case "search":
            return <svg {...props}><circle {...line} cx="10.8" cy="10.8" r="5.8" /><path {...line} d="m15.2 15.2 4.1 4.1" /></svg>;
        case "refresh":
            return <svg {...props}><path {...line} d="M19 7.4A8 8 0 0 0 5.2 7L4 9.2" /><path {...line} d="M4 4.8v4.4h4.4" /><path {...line} d="M5 16.6a8 8 0 0 0 13.8.4l1.2-2.2" /><path {...line} d="M20 19.2v-4.4h-4.4" /></svg>;
        case "lock":
            return <svg {...props}><rect {...line} x="5" y="10" width="14" height="10" rx="2.5" /><path {...line} d="M8 10V7.7a4 4 0 0 1 8 0V10" /><path {...line} d="M12 14.2v2.2" /></svg>;
        case "eye":
            return <svg {...props}><path {...line} d="M3 12s3.4-5.6 9-5.6S21 12 21 12s-3.4 5.6-9 5.6S3 12 3 12Z" /><circle {...line} cx="12" cy="12" r="2.5" /></svg>;
        case "spark":
            return <svg {...props}><path {...line} d="M12 3.5 13.7 9l5.8 1.7-5.8 1.7L12 20l-1.7-7.6-5.8-1.7L10.3 9 12 3.5Z" /></svg>;
        case "check":
            return <svg {...props}><path {...line} d="m5 12.5 4.2 4.1L19 7" /></svg>;
        case "arrow":
            return <svg {...props}><path {...line} d="M7 17 17 7" /><path {...line} d="M9 7h8v8" /></svg>;
        case "image":
            return <svg {...props}><rect {...line} x="4" y="5" width="16" height="14" rx="2.5" /><path {...line} d="m7 16 3.4-3.4 2.4 2.4 2.2-2.2L19 16" /><circle cx="9" cy="9" r="1.3" fill="currentColor" /></svg>;
        case "link":
            return <svg {...props}><path {...line} d="M10 13.7a4 4 0 0 0 5.7 0l2.2-2.2a4 4 0 0 0-5.7-5.7l-1.1 1.1" /><path {...line} d="M14 10.3a4 4 0 0 0-5.7 0l-2.2 2.2a4 4 0 0 0 5.7 5.7l1.1-1.1" /></svg>;
        case "upload":
            return <svg {...props}><path {...line} d="M12 15V4" /><path {...line} d="m8 8 4-4 4 4" /><path {...line} d="M5 15v3.2A1.8 1.8 0 0 0 6.8 20h10.4a1.8 1.8 0 0 0 1.8-1.8V15" /></svg>;
        case "note":
            return <svg {...props}><path {...line} d="M6 4h9l3 3v13H6V4Z" /><path {...line} d="M15 4v4h4M9 12h6M9 15.5h6" /></svg>;
        case "settings":
            return <svg {...props}><path {...line} d="M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z" /><path {...line} d="M19 12a7 7 0 0 0-.1-1l2-1.6-2-3.4-2.4 1a7.5 7.5 0 0 0-1.7-1L14.5 3h-5l-.3 3a7.5 7.5 0 0 0-1.7 1l-2.4-1-2 3.4 2 1.6a7 7 0 0 0 0 2l-2 1.6 2 3.4 2.4-1c.5.4 1.1.7 1.7 1l.3 3h5l.3-3c.6-.2 1.2-.6 1.7-1l2.4 1 2-3.4-2-1.6c.1-.3.1-.7.1-1Z" /></svg>;
        case "leaf":
            return <svg {...props}><path {...line} d="M20 4c-6.8.4-12.7 3.6-13.8 9.5-.6 3.4 1.9 5.9 5.3 5.3C17.4 17.8 19.7 10.8 20 4Z" /><path {...line} d="M6 18c2.8-4.7 6.2-7.4 10.4-9" /></svg>;
        case "info":
            return <svg {...props}><circle {...line} cx="12" cy="12" r="9" /><path {...line} d="M12 10.5v5" /><path {...line} d="M12 7.5h.01" /></svg>;
        case "close":
            return <svg {...props}><path {...line} d="M6 6l12 12M18 6 6 18" /></svg>;
        default:
            return <svg {...props}><circle {...line} cx="12" cy="12" r="8" /></svg>;
    }
}

// ─── Секции формы ─────────────────────────────────────────────────────────────
const SECTIONS = [
    { id: "main", label: "Основное", caption: "Ключевые параметры", icon: "note" },
    { id: "schedule", label: "Расписание", caption: "Дата и время", icon: "calendar" },
    { id: "participants", label: "Участники", caption: "Условия участия", icon: "users" },
    { id: "design", label: "Оформление", caption: "Визуал и детали", icon: "image" },
];

const STEP_INDEX = SECTIONS.reduce((acc, item, idx) => ({ ...acc, [item.id]: idx }), {});

// ─── Инлайн-стили компактной вкладочной модалки ──────────────────────────────
const MODAL_STYLES = `
.b-gsm.b-modal__card {
    font-family: 'Circe', sans-serif;
    width: min(900px, calc(100vw - 32px));
    margin: 8vh auto 0;
    max-height: calc(100vh - 72px);
    display: flex;
    flex-direction: column;
    border-radius: 22px;
    overflow: hidden;
    background: #fff;
    color: #313235;
    box-shadow: 0 26px 70px rgba(24, 26, 32, .22);
}

.b-gsm * { box-sizing: border-box; }

.b-gsm__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 18px;
    padding: 26px 28px 16px;
    border-bottom: 1px solid rgba(210,215,219,.62);
}

.b-gsm__title {
    font-size: 23px;
    line-height: 1.14;
    font-weight: 700;
    letter-spacing: -.015em;
}

.b-gsm__subtitle {
    margin-top: 8px;
    font-size: 15px;
    line-height: 1.38;
    color: rgba(49,50,53,.66);
}

.b-gsm__close {
    width: 40px;
    height: 40px;
    border-radius: 14px;
    border: 1px solid rgba(210,215,219,.82);
    background: #fff;
    color: rgba(49,50,53,.72);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: all .18s ease;
}

.b-gsm__close:hover {
    color: #313235;
    border-color: rgba(172,170,254,.58);
    box-shadow: 0 8px 20px rgba(172,170,254,.15);
}

.b-gsm__tabs {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin: 0 28px;
    padding: 0;
    border: 1px solid rgba(210,215,219,.62);
    border-radius: 16px;
    overflow: hidden;
    background: rgba(248,249,251,.82);
}

.b-gsm__tab {
    position: relative;
    min-height: 54px;
    border: 0;
    border-right: 1px solid rgba(210,215,219,.56);
    background: transparent;
    color: rgba(49,50,53,.72);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all .18s ease;
}

.b-gsm__tab:last-child { border-right: 0; }

.b-gsm__tab::after {
    content: "";
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 0;
    height: 2px;
    border-radius: 2px;
    background: transparent;
}

.b-gsm__tab:hover {
    color: #313235;
    background: rgba(255,255,255,.72);
}

.b-gsm__tab.is-active {
    color: #313235;
    background: linear-gradient(180deg, rgba(230,247,239,.98), rgba(232,247,240,.86));
}

.b-gsm__tab.is-active::after {
    background: #62C08B;
}

.b-gsm__tab .gs-icon { color: currentColor; }

.b-gsm__tab-err {
    position: absolute;
    right: 12px;
    top: 10px;
    width: 7px;
    height: 7px;
    border-radius: 99px;
    background: #F26F55;
}

.b-gsm__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 20px 28px 18px;
    scrollbar-width: thin;
    scrollbar-color: #D2D7DB transparent;
}

.b-gsm__panel {
    display: grid;
    gap: 18px;
}

.b-gsm__panel--main {
    grid-template-columns: minmax(0, 1fr) 292px;
    align-items: start;
}

.b-gsm__stack {
    display: grid;
    gap: 15px;
}

.b-gsm__field {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.b-gsm__field-label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    line-height: 1.2;
    font-weight: 700;
    color: #313235;
}

.b-gsm__field-hint {
    font-size: 13px;
    line-height: 1.4;
    color: rgba(49,50,53,.52);
}

.b-gsm__field-foot {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    font-size: 13px;
    line-height: 1.35;
    color: rgba(49,50,53,.5);
}

.b-gsm .b-input,
.b-gsm textarea.b-input {
    width: 100%;
    min-height: 46px;
    border: 1px solid rgba(210,215,219,.86);
    border-radius: 13px;
    padding: 0 14px;
    background: #fff;
    color: #313235;
    font-family: inherit;
    font-size: 15px;
    line-height: 1.42;
    outline: none;
    transition: border-color .18s ease, box-shadow .18s ease;
}

.b-gsm textarea.b-input {
    min-height: 92px;
    padding: 13px 14px;
    resize: vertical;
}

.b-gsm .b-input:focus,
.b-gsm textarea.b-input:focus {
    border-color: rgba(172,170,254,.9);
    box-shadow: 0 0 0 3px rgba(172,170,254,.14);
}

.b-gsm .b-input.is-error {
    border-color: rgba(242,111,85,.92);
    box-shadow: 0 0 0 3px rgba(242,111,85,.12);
}

.b-gs-form__err {
    font-size: 13px;
    line-height: 1.35;
    color: #F26F55;
}

.b-gsm__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}

.b-gsm__chip {
    min-height: 32px;
    padding: 6px 12px;
    border-radius: 10px;
    border: 1px solid rgba(210,215,219,.76);
    background: #fff;
    color: rgba(49,50,53,.72);
    font-family: inherit;
    font-size: 14px;
    line-height: 1.2;
    cursor: pointer;
    transition: all .18s ease;
}

.b-gsm__chip:hover {
    border-color: rgba(172,170,254,.62);
    color: #313235;
}

.b-gsm__chip.is-active {
    border-color: rgba(172,170,254,.3);
    background: rgba(172,170,254,.16);
    color: #644DFF;
    font-weight: 700;
}

.b-gsm__chip--green.is-active {
    border-color: rgba(98,192,139,.32);
    background: rgba(98,192,139,.15);
    color: #2d8a56;
}

.b-gsm__chip--add {
    width: 32px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
}

.b-gsm__segmented {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 4px;
    padding: 4px;
    border: 1px solid rgba(210,215,219,.82);
    border-radius: 14px;
    background: #fff;
}

.b-gsm__segment {
    min-height: 38px;
    border: 0;
    border-radius: 10px;
    background: transparent;
    color: rgba(49,50,53,.68);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
    transition: all .18s ease;
}

.b-gsm__segment.is-active {
    color: #313235;
    background: rgba(248,250,252,.98);
    box-shadow: 0 6px 14px rgba(33,37,41,.08);
}

.b-gsm__preview {
    border: 1px solid rgba(210,215,219,.66);
    border-radius: 18px;
    padding: 12px;
    background: #fff;
}

.b-gsm__preview-label {
    margin-bottom: 10px;
    font-size: 14px;
    font-weight: 700;
    color: #313235;
}

.b-gsm__preview-cover {
    height: 118px;
    border-radius: 16px;
    overflow: hidden;
    background:
      radial-gradient(circle at 26% 44%, rgba(212,201,255,.74) 0 20%, transparent 20.5%),
      radial-gradient(circle at 76% 26%, rgba(255,224,226,.72) 0 34%, transparent 34.5%),
      linear-gradient(135deg, rgba(246,240,255,.96), rgba(255,235,239,.92) 54%, rgba(238,245,252,.96));
}

.b-gsm__preview-cover img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.b-gsm__preview-title {
    margin-top: 12px;
    font-size: 17px;
    line-height: 1.2;
    font-weight: 700;
    color: #313235;
}

.b-gsm__preview-meta {
    margin-top: 9px;
    display: flex;
    flex-wrap: wrap;
    gap: 9px 12px;
    color: rgba(49,50,53,.58);
    font-size: 13px;
    line-height: 1.3;
}

.b-gsm__preview-meta span {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

.b-gsm__preview-status {
    margin-top: 13px;
    display: inline-flex;
    min-height: 28px;
    align-items: center;
    padding: 0 11px;
    border-radius: 999px;
    background: rgba(98,192,139,.18);
    color: #2d8a56;
    font-size: 13px;
    font-weight: 700;
}

.b-gsm__preview-note {
    margin-top: 12px;
    font-size: 13px;
    line-height: 1.45;
    color: rgba(49,50,53,.52);
}

.b-gsm__icon-input {
    position: relative;
}

.b-gsm__icon-input .gs-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(49,50,53,.48);
}

.b-gsm__icon-input .b-input {
    padding-left: 42px;
}

.b-gsm__section-card {
    border: 1px solid rgba(210,215,219,.62);
    border-radius: 18px;
    padding: 18px;
    background: #fff;
}

.b-gsm__section-title {
    margin-bottom: 14px;
    font-size: 16px;
    line-height: 1.25;
    font-weight: 700;
    color: #313235;
}

.b-gsm__grid {
    display: grid;
    gap: 16px;
}

.b-gsm__grid--three {
    grid-template-columns: repeat(3, minmax(0, 1fr));
}

.b-gsm__grid--two {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.b-gsm__cover-row {
    display: grid;
    grid-template-columns: minmax(220px, 1fr) minmax(260px, 1fr);
    gap: 14px;
}

.b-gsm__cover-preview {
    min-height: 176px;
    border: 1px solid rgba(210,215,219,.66);
    border-radius: 18px;
    overflow: hidden;
    background:
      radial-gradient(circle at 26% 44%, rgba(212,201,255,.74) 0 20%, transparent 20.5%),
      radial-gradient(circle at 76% 26%, rgba(255,224,226,.72) 0 34%, transparent 34.5%),
      linear-gradient(135deg, rgba(246,240,255,.96), rgba(255,235,239,.92) 54%, rgba(238,245,252,.96));
}

.b-gsm__cover-preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.b-gsm__cover-upload {
    min-height: 176px;
    border: 1.5px dashed rgba(210,215,219,.96);
    border-radius: 18px;
    background: #fff;
    display: flex;
    flex-direction: column;
    gap: 7px;
    align-items: center;
    justify-content: center;
    padding: 18px;
    text-align: center;
    color: rgba(49,50,53,.62);
    cursor: pointer;
    transition: all .18s ease;
}

.b-gsm__cover-upload:hover,
.b-gsm__cover-upload.is-drag {
    border-color: rgba(172,170,254,.72);
    background: rgba(172,170,254,.05);
}

.b-gsm__cover-upload strong {
    color: #313235;
    font-size: 15px;
}

.b-gsm__cover-upload small {
    color: rgba(49,50,53,.52);
    font-size: 13px;
    line-height: 1.36;
}

.b-gsm__cover-switch {
    display: inline-flex;
    gap: 8px;
    margin-bottom: 14px;
}

.b-gsm__cover-switch-btn {
    min-height: 34px;
    padding: 0 12px;
    border-radius: 10px;
    border: 1px solid rgba(210,215,219,.82);
    background: #fff;
    color: rgba(49,50,53,.64);
    font-family: inherit;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
}

.b-gsm__cover-switch-btn.is-active {
    border-color: rgba(172,170,254,.35);
    background: rgba(172,170,254,.13);
    color: #644DFF;
}

.b-gsm__url-wrap {
    position: relative;
    margin-bottom: 14px;
}

.b-gsm__url-wrap .gs-icon {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: rgba(49,50,53,.48);
}

.b-gsm__url-wrap .b-input { padding-left: 42px; }

.b-gsm__toggle-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-top: 16px;
    min-height: 58px;
    padding: 14px 16px;
    border: 1px solid rgba(210,215,219,.66);
    border-radius: 16px;
    background: #fff;
}

.b-gsm__toggle-title {
    font-size: 15px;
    line-height: 1.25;
    font-weight: 700;
}

.b-gsm__toggle-desc {
    margin-top: 3px;
    font-size: 13px;
    line-height: 1.38;
    color: rgba(49,50,53,.52);
}

.b-gsm__switch {
    position: relative;
    width: 48px;
    height: 28px;
    border: 0;
    border-radius: 999px;
    background: rgba(210,215,219,.9);
    flex-shrink: 0;
    cursor: pointer;
    transition: background .18s ease;
}

.b-gsm__switch::after {
    content: "";
    position: absolute;
    left: 3px;
    top: 3px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 3px 8px rgba(0,0,0,.13);
    transition: transform .18s ease;
}

.b-gsm__switch.is-on {
    background: linear-gradient(90deg, #62C08B, #45BE78);
}

.b-gsm__switch.is-on::after {
    transform: translateX(20px);
}

.b-gsm__foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    padding: 16px 28px 22px;
    border-top: 1px solid rgba(210,215,219,.62);
    background: rgba(255,255,255,.98);
}

.b-gsm__progress {
    display: flex;
    align-items: center;
    gap: 16px;
    color: rgba(49,50,53,.58);
    font-size: 14px;
    font-weight: 600;
}

.b-gsm__dots {
    display: inline-flex;
    align-items: center;
    gap: 12px;
}

.b-gsm__dot {
    width: 7px;
    height: 7px;
    border-radius: 999px;
    background: rgba(210,215,219,.95);
}

.b-gsm__dot.is-active {
    width: 10px;
    height: 10px;
    background: #62C08B;
}

.b-gsm__foot-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-left: auto;
}

.b-gsm__btn {
    min-width: 118px;
    min-height: 44px;
    padding: 0 18px;
    border: 1px solid rgba(210,215,219,.85);
    border-radius: 14px;
    background: #fff;
    color: #313235;
    font-family: inherit;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    transition: all .18s ease;
}

.b-gsm__btn:hover { box-shadow: 0 8px 18px rgba(49,50,53,.08); }

.b-gsm__btn--primary {
    border-color: #2E3138;
    background: linear-gradient(180deg, #383B43, #26282E);
    color: #fff;
    box-shadow: 0 13px 23px rgba(38,40,46,.2);
}

.b-gsm__btn--primary:disabled,
.b-gsm__btn:disabled {
    opacity: .6;
    cursor: not-allowed;
    box-shadow: none;
}

.b-gsm__spinner {
    width: 15px;
    height: 15px;
    border-radius: 50%;
    border: 2px solid rgba(255,255,255,.45);
    border-top-color: #fff;
    display: inline-block;
    animation: b-gsm-spin .75s linear infinite;
    margin-right: 8px;
    vertical-align: -2px;
}

@keyframes b-gsm-spin { to { transform: rotate(360deg); } }

[data-app-theme="dark"] .b-gsm.b-modal__card {
    background: linear-gradient(180deg, rgba(14, 20, 32, .98), rgba(10, 16, 27, .98));
    color: #E9EEFB;
    border: 1px solid rgba(128,146,176,.34);
    box-shadow: 0 30px 70px rgba(0,0,0,.45);
}

[data-app-theme="dark"] .b-gsm__head,
[data-app-theme="dark"] .b-gsm__foot {
    border-color: rgba(128,146,176,.34);
}

[data-app-theme="dark"] .b-gsm__subtitle,
[data-app-theme="dark"] .b-gsm__field-hint,
[data-app-theme="dark"] .b-gsm__field-foot,
[data-app-theme="dark"] .b-gsm__preview-note,
[data-app-theme="dark"] .b-gsm__toggle-desc,
[data-app-theme="dark"] .b-gsm__progress {
    color: rgba(226,233,248,.72);
}

[data-app-theme="dark"] .b-gsm__title,
[data-app-theme="dark"] .b-gsm__field-label,
[data-app-theme="dark"] .b-gsm__section-title,
[data-app-theme="dark"] .b-gsm__preview-label,
[data-app-theme="dark"] .b-gsm__preview-title,
[data-app-theme="dark"] .b-gsm__toggle-title {
    color: #E9EEFB;
}

[data-app-theme="dark"] .b-gsm__close {
    background: rgba(22,31,47,.92);
    border-color: rgba(128,146,176,.42);
    color: rgba(226,233,248,.84);
}

[data-app-theme="dark"] .b-gsm__close:hover {
    color: #F4F7FF;
    border-color: rgba(172,170,254,.62);
    box-shadow: 0 10px 24px rgba(172,170,254,.22);
}

[data-app-theme="dark"] .b-gsm__tabs {
    border-color: rgba(128,146,176,.36);
    background: rgba(17,25,39,.86);
}

[data-app-theme="dark"] .b-gsm__tab {
    color: rgba(226,233,248,.78);
    border-color: rgba(128,146,176,.3);
}

[data-app-theme="dark"] .b-gsm__tab:hover {
    color: #E9EEFB;
    background: rgba(32,44,66,.78);
}

[data-app-theme="dark"] .b-gsm__tab.is-active {
    color: #E9EEFB;
    background: linear-gradient(180deg, rgba(37,64,59,.7), rgba(29,51,47,.55));
}

[data-app-theme="dark"] .b-gsm__tab-err {
    background: #FF7E66;
}

[data-app-theme="dark"] .b-gsm .b-input,
[data-app-theme="dark"] .b-gsm textarea.b-input {
    border-color: rgba(128,146,176,.42);
    background: rgba(19,28,43,.9);
    color: #E9EEFB;
}

[data-app-theme="dark"] .b-gsm .b-input::placeholder,
[data-app-theme="dark"] .b-gsm textarea.b-input::placeholder {
    color: rgba(226,233,248,.46);
}

[data-app-theme="dark"] .b-gsm .b-input:focus,
[data-app-theme="dark"] .b-gsm textarea.b-input:focus {
    border-color: rgba(172,170,254,.9);
    box-shadow: 0 0 0 3px rgba(172,170,254,.2);
}

[data-app-theme="dark"] .b-gsm__chips .b-gsm__chip {
    border-color: rgba(128,146,176,.46);
    background: rgba(19,28,43,.88);
    color: rgba(226,233,248,.82);
}

[data-app-theme="dark"] .b-gsm__chip:hover {
    border-color: rgba(172,170,254,.66);
    color: #E9EEFB;
}

[data-app-theme="dark"] .b-gsm__chip.is-active {
    border-color: rgba(172,170,254,.52);
    background: rgba(172,170,254,.22);
    color: #DCD6FF;
}

[data-app-theme="dark"] .b-gsm__chip--green.is-active {
    border-color: rgba(115,227,178,.5);
    background: rgba(115,227,178,.2);
    color: #9BF2CD;
}

[data-app-theme="dark"] .b-gsm__segmented {
    border-color: rgba(128,146,176,.42);
    background: rgba(19,28,43,.88);
}

[data-app-theme="dark"] .b-gsm__segment {
    color: rgba(226,233,248,.75);
}

[data-app-theme="dark"] .b-gsm__segment.is-active {
    color: #E9EEFB;
    background: rgba(43,57,80,.95);
    box-shadow: 0 6px 16px rgba(0,0,0,.3);
}

[data-app-theme="dark"] .b-gsm__preview,
[data-app-theme="dark"] .b-gsm__section-card,
[data-app-theme="dark"] .b-gsm__toggle-row,
[data-app-theme="dark"] .b-gsm__cover-preview,
[data-app-theme="dark"] .b-gsm__cover-upload {
    border-color: rgba(128,146,176,.38);
    background: rgba(17,25,39,.88);
}

[data-app-theme="dark"] .b-gsm__preview-meta {
    color: rgba(226,233,248,.66);
}

[data-app-theme="dark"] .b-gsm__preview-status {
    background: rgba(115,227,178,.22);
    color: #9BF2CD;
}

[data-app-theme="dark"] .b-gsm__icon-input .gs-icon,
[data-app-theme="dark"] .b-gsm__url-wrap .gs-icon {
    color: rgba(226,233,248,.56);
}

[data-app-theme="dark"] .b-gsm__cover-upload strong {
    color: #E9EEFB;
}

[data-app-theme="dark"] .b-gsm__cover-upload small {
    color: rgba(226,233,248,.66);
}

[data-app-theme="dark"] .b-gsm__cover-switch-btn {
    border-color: rgba(128,146,176,.42);
    background: rgba(19,28,43,.88);
    color: rgba(226,233,248,.76);
}

[data-app-theme="dark"] .b-gsm__cover-switch-btn.is-active {
    border-color: rgba(172,170,254,.56);
    background: rgba(172,170,254,.24);
    color: #DCD6FF;
}

[data-app-theme="dark"] .b-gsm__switch {
    background: rgba(95,108,132,.72);
}

[data-app-theme="dark"] .b-gsm__switch::after {
    background: #E9EEFB;
}

[data-app-theme="dark"] .b-gsm__foot {
    background: rgba(12,18,29,.95);
}

[data-app-theme="dark"] .b-gsm__dot {
    background: rgba(95,108,132,.9);
}

[data-app-theme="dark"] .b-gsm__btn {
    border-color: rgba(128,146,176,.46);
    background: rgba(29,40,59,.94);
    color: #E9EEFB;
}

[data-app-theme="dark"] .b-gsm__btn:hover {
    box-shadow: 0 10px 20px rgba(0,0,0,.3);
    background: rgba(43,57,80,.98);
}

[data-app-theme="dark"] .b-gsm__btn--primary {
    border-color: rgba(115,227,178,.76);
    background: linear-gradient(180deg, #73E3B2, #54C997);
    color: #0E1A14;
    box-shadow: 0 14px 26px rgba(12, 40, 30, .38);
}

[data-app-theme="dark"] .b-gsm__btn--primary .b-gsm__spinner {
    border-color: rgba(14,26,20,.35);
    border-top-color: #0E1A14;
}

@media (max-width: 940px) {
    .b-gsm.b-modal__card { width: min(760px, calc(100vw - 24px)); margin-top: 24px; }
    .b-gsm__panel--main,
    .b-gsm__cover-row { grid-template-columns: 1fr; }
    .b-gsm__tabs { grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .b-gsm__tab:nth-child(2) { border-right: 0; }
    .b-gsm__tab:nth-child(-n+2) { border-bottom: 1px solid rgba(210,215,219,.56); }
    .b-gsm__grid--three { grid-template-columns: 1fr; }
}

@media (max-width: 620px) {
    .b-gsm.b-modal__card {
        width: calc(100vw - 12px);
        margin-top: 8px;
        max-height: calc(100vh - 16px);
        border-radius: 18px;
    }
    .b-gsm__head,
    .b-gsm__body,
    .b-gsm__foot { padding-left: 16px; padding-right: 16px; }
    .b-gsm__tabs { margin: 0 16px; }
    .b-gsm__tab { justify-content: flex-start; padding: 0 12px; }
    .b-gsm__tab span:last-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .b-gsm__grid--two { grid-template-columns: 1fr; }
    .b-gsm__foot { flex-direction: column; align-items: stretch; }
    .b-gsm__foot-actions { width: 100%; margin-left: 0; }
    .b-gsm__btn { flex: 1; width: 100%; }
}
`;

// ─── Компонент EditModal ──────────────────────────────────────────────────────
function EditModal({ open, draft, setDraft, saving, onClose, onSave, allThemes, allMethods }) {
    const [sec, setSec] = useState("main");
    const [touched, setT] = useState({});
    const [drag, setDrag] = useState(false);
    const fileRef = useRef(null);
    const coverUploading = draft._coverUploading || false;

    useEffect(() => {
        if (open) {
            setSec("main");
            setT({});
        }
    }, [open]);

    const setD = (k, v) => setDraft(d => ({ ...d, [k]: v }));
    const touch = k => setT(p => ({ ...p, [k]: true }));
    const togTag = (k, id) => setDraft(d => ({
        ...d,
        [k]: (d[k] || []).includes(id) ? (d[k] || []).filter(x => x !== id) : [...(d[k] || []), id],
    }));

    const errs = {};
    if (!draft.title?.trim()) errs.title = "Укажите название";
    if (!draft.date) errs.date = "Укажите дату";
    if (!draft.timeStart) errs.timeStart = "Укажите время начала";
    else if (!isValidTime(draft.timeStart)) errs.timeStart = "Формат: ЧЧ:ММ, например 18:00";
    if (draft.timeEnd && !isValidTime(draft.timeEnd)) errs.timeEnd = "Формат: ЧЧ:ММ, например 19:30";

    const activeIndex = Math.max(0, STEP_INDEX[sec] ?? 0);
    const isLast = activeIndex === SECTIONS.length - 1;
    const mainHasErr = Boolean((touched.title && errs.title) || (touched.date && errs.date) || (touched.timeStart && errs.timeStart));
    const canSave = Object.keys(errs).length === 0;
    const activeCover = draft.coverMode === "url" ? draft.coverUrl : (draft.cover || draft.coverImg);
    const localFormat = String(draft.sessionFormat || "ONLINE").toUpperCase();
    const descriptionCount = String(draft.description || "").length;
    const previewDate = (() => {
        if (!draft.date) return "Дата и время";
        try {
            const d = new Date(draft.date);
            const dateText = d.toLocaleDateString("ru-RU", { day: "2-digit", month: "short" }).replace(".", "");
            return draft.timeStart ? `${dateText} · ${draft.timeStart}` : dateText;
        } catch {
            return "Дата и время";
        }
    })();

    const goNext = () => {
        if (sec === "main") {
            setT(p => ({ ...p, title: true }));
            if (errs.title) return;
        }
        if (sec === "schedule") {
            setT(p => ({ ...p, date: true, timeStart: true, timeEnd: true }));
            if (errs.date || errs.timeStart || errs.timeEnd) return;
        }
        if (!isLast) setSec(SECTIONS[activeIndex + 1].id);
    };

    const goPrev = () => {
        if (activeIndex > 0) setSec(SECTIONS[activeIndex - 1].id);
    };

    const onFileSelected = async (file) => {
        if (!file) return;
        const MAX = 5 * 1024 * 1024;
        if (file.size > MAX) return;
        setDraft(d => ({ ...d, _coverUploading: true }));
        try {
            const r = await filesApi.uploadImage(file);
            const url = r?.file?.url || r?.url || r?.avatarUrl || null;
            if (!url) throw new Error("Не удалось получить URL");
            const stored = toStoredFilePath(url);
            setDraft(d => ({ ...d, cover: stored, coverImg: stored, _coverUploading: false }));
        } catch {
            setDraft(d => ({ ...d, _coverUploading: false }));
        }
    };

    const onDrop = useCallback((e) => {
        e.preventDefault();
        setDrag(false);
        const file = e.dataTransfer?.files?.[0];
        if (file?.type?.startsWith("image/")) onFileSelected(file);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    if (!open) return null;

    const renderPreview = () => (
        <aside className="b-gsm__preview">
            <div className="b-gsm__preview-label">Предпросмотр</div>
            <div className="b-gsm__preview-cover">
                {activeCover ? <img src={activeCover.startsWith("/") ? resolveUrl(activeCover) : activeCover} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : null}
            </div>
            <div className="b-gsm__preview-title">{draft.title?.trim() || "Название сессии"}</div>
            <div className="b-gsm__preview-meta">
                <span><GsIcon name="calendar" size={14} /> {previewDate}</span>
                <span><GsIcon name={localFormat === "OFFLINE" ? "pin" : "wifi"} size={14} /> {localFormat === "OFFLINE" ? "Офлайн" : "Онлайн"}</span>
            </div>
            <div className="b-gsm__preview-status">Открыта</div>
            <div className="b-gsm__preview-note">Детали, участники и условия записи будут добавлены на следующих вкладках.</div>
        </aside>
    );

    return (
        <div className="b-modal" role="dialog" aria-modal="true">
            <style>{MODAL_STYLES}</style>
            <button className="b-modal__backdrop" type="button" onClick={onClose} aria-label="Закрыть" />

            <div className="b-modal__card b-gsm">
                <div className="b-gsm__head">
                    <div>
                        <div className="b-gsm__title">{draft.id ? "Редактирование групповой сессии" : "Создание групповой сессии"}</div>
                        <div className="b-gsm__subtitle">Заполните основные параметры и переходите между вкладками.</div>
                    </div>
                    <button className="b-gsm__close" type="button" onClick={onClose} aria-label="Закрыть">
                        <GsIcon name="close" size={18} />
                    </button>
                </div>

                <nav className="b-gsm__tabs" aria-label="Разделы формы">
                    {SECTIONS.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            className={`b-gsm__tab ${sec === item.id ? "is-active" : ""}`}
                            onClick={() => setSec(item.id)}
                        >
                            <GsIcon name={item.icon} size={18} />
                            <span>{item.label}</span>
                            {item.id === "main" && mainHasErr ? <span className="b-gsm__tab-err" /> : null}
                        </button>
                    ))}
                </nav>

                <div className="b-gsm__body">
                    {sec === "main" && (
                        <div className="b-gsm__panel b-gsm__panel--main">
                            <div className="b-gsm__stack">
                                <div className="b-gsm__field">
                                    <div className="b-gsm__field-label">Название сессии</div>
                                    <input
                                        className={`b-input ${touched.title && errs.title ? "is-error" : ""}`}
                                        value={draft.title || ""}
                                        maxLength={100}
                                        onChange={(e) => setD("title", e.target.value)}
                                        onBlur={() => touch("title")}
                                        placeholder="Введите название сессии"
                                    />
                                    <div className="b-gsm__field-foot">
                                        <span>{touched.title && errs.title ? <span className="b-gs-form__err">{errs.title}</span> : " "}</span>
                                        <span>{String(draft.title || "").length}/100</span>
                                    </div>
                                </div>

                                <div className="b-gsm__field">
                                    <div className="b-gsm__field-label">Краткое описание</div>
                                    <textarea
                                        className="b-input"
                                        maxLength={300}
                                        value={draft.description || ""}
                                        onChange={(e) => setD("description", e.target.value)}
                                        placeholder="Кратко опишите цель и формат встречи"
                                    />
                                    <div className="b-gsm__field-foot">
                                        <span>Описание поможет участникам понять формат встречи.</span>
                                        <span>{descriptionCount}/300</span>
                                    </div>
                                </div>

                                <div className="b-gsm__field">
                                    <div className="b-gsm__field-label">Темы <GsIcon name="info" size={14} /></div>
                                    <div className="b-gsm__chips">
                                        {(allThemes || []).slice(0, 10).map((item) => {
                                            const id = item?.id ?? item?.value ?? item?.code;
                                            const title = item?.title || item?.name || item?.label || `Тема ${id}`;
                                            const active = (draft.themeIds || []).includes(id);
                                            return (
                                                <button key={id} type="button" className={`b-gsm__chip ${active ? "is-active" : ""}`} onClick={() => togTag("themeIds", id)}>
                                                    {title}
                                                </button>
                                            );
                                        })}
                                        <button type="button" className="b-gsm__chip b-gsm__chip--add" aria-label="Добавить тему">+</button>
                                    </div>
                                    <div className="b-gsm__field-hint">Добавьте темы, чтобы участникам было проще найти встречу.</div>
                                </div>

                                <div className="b-gsm__field">
                                    <div className="b-gsm__field-label">Формат</div>
                                    <div className="b-gsm__segmented">
                                        <button type="button" className={`b-gsm__segment ${localFormat === "ONLINE" ? "is-active" : ""}`} onClick={() => setD("sessionFormat", "ONLINE")}>
                                            <GsIcon name="wifi" size={16} /> Онлайн
                                        </button>
                                        <button type="button" className={`b-gsm__segment ${localFormat === "OFFLINE" ? "is-active" : ""}`} onClick={() => setD("sessionFormat", "OFFLINE")}>
                                            <GsIcon name="pin" size={16} /> Офлайн
                                        </button>
                                    </div>
                                </div>
                            </div>
                            {renderPreview()}
                        </div>
                    )}

                    {sec === "schedule" && (
                        <div className="b-gsm__panel">
                            <section className="b-gsm__section-card">
                                <div className="b-gsm__section-title">Расписание</div>
                                <div className="b-gsm__grid b-gsm__grid--three">
                                    <div className="b-gsm__field">
                                        <div className="b-gsm__field-label">Дата</div>
                                        <div className="b-gsm__icon-input">
                                            <GsIcon name="calendar" size={16} />
                                            <input
                                                type="date"
                                                className={`b-input ${touched.date && errs.date ? "is-error" : ""}`}
                                                value={draft.date || ""}
                                                onChange={(e) => setD("date", e.target.value)}
                                                onBlur={() => touch("date")}
                                            />
                                        </div>
                                        {touched.date && errs.date ? <div className="b-gs-form__err">{errs.date}</div> : null}
                                    </div>
                                    <div className="b-gsm__field">
                                        <div className="b-gsm__field-label">Начало</div>
                                        <div className="b-gsm__icon-input">
                                            <GsIcon name="calendar" size={16} />
                                            <input
                                                className={`b-input ${touched.timeStart && errs.timeStart ? "is-error" : ""}`}
                                                value={draft.timeStart || ""}
                                                onChange={(e) => setD("timeStart", maskTime(e.target.value))}
                                                onBlur={() => touch("timeStart")}
                                                placeholder="10:00"
                                                inputMode="numeric"
                                            />
                                        </div>
                                        {touched.timeStart && errs.timeStart ? <div className="b-gs-form__err">{errs.timeStart}</div> : null}
                                    </div>
                                    <div className="b-gsm__field">
                                        <div className="b-gsm__field-label">Окончание</div>
                                        <div className="b-gsm__icon-input">
                                            <GsIcon name="calendar" size={16} />
                                            <input
                                                className={`b-input ${touched.timeEnd && errs.timeEnd ? "is-error" : ""}`}
                                                value={draft.timeEnd || ""}
                                                onChange={(e) => setD("timeEnd", maskTime(e.target.value))}
                                                onBlur={() => touch("timeEnd")}
                                                placeholder="11:30"
                                                inputMode="numeric"
                                            />
                                        </div>
                                        {touched.timeEnd && errs.timeEnd ? <div className="b-gs-form__err">{errs.timeEnd}</div> : null}
                                    </div>
                                </div>
                                <div className="b-gsm__field-hint" style={{ marginTop: 14 }}>Укажите время с учётом часового пояса. Участники увидят дату и длительность в карточке сессии.</div>
                            </section>
                        </div>
                    )}

                    {sec === "participants" && (
                        <div className="b-gsm__panel">
                            <section className="b-gsm__section-card">
                                <div className="b-gsm__section-title">Участники и условия записи</div>
                                <div className="b-gsm__grid b-gsm__grid--three">
                                    <div className="b-gsm__field">
                                        <div className="b-gsm__field-label">Макс. участников</div>
                                        <div className="b-gsm__icon-input">
                                            <GsIcon name="users" size={16} />
                                            <input className="b-input" type="number" min="1" value={draft.capacityClients ?? ""} onChange={(e) => setD("capacityClients", e.target.value)} placeholder="20" />
                                        </div>
                                        <div className="b-gsm__field-hint">Максимальное количество участников</div>
                                    </div>
                                    <div className="b-gsm__field">
                                        <div className="b-gsm__field-label">Мин. участников</div>
                                        <div className="b-gsm__icon-input">
                                            <GsIcon name="users" size={16} />
                                            <input className="b-input" type="number" min="1" value={draft.minCapacity ?? ""} onChange={(e) => setD("minCapacity", e.target.value)} placeholder="3" />
                                        </div>
                                        <div className="b-gsm__field-hint">Минимум для проведения</div>
                                    </div>
                                    <div className="b-gsm__field">
                                        <div className="b-gsm__field-label">Стоимость</div>
                                        <div className="b-gsm__icon-input">
                                            <GsIcon name="tag" size={16} />
                                            <input className="b-input" type="number" min="0" step="1" value={draft.priceAtTime ?? ""} onChange={(e) => setD("priceAtTime", e.target.value)} placeholder="0" />
                                        </div>
                                        <div className="b-gsm__field-hint">Оставьте 0 ₽ для бесплатной сессии</div>
                                    </div>
                                </div>
                                <div className="b-gsm__toggle-row">
                                    <div>
                                        <div className="b-gsm__toggle-title">Открыть запись сразу</div>
                                        <div className="b-gsm__toggle-desc">Участники смогут записываться сразу после публикации сессии.</div>
                                    </div>
                                    <button type="button" className={`b-gsm__switch ${draft.allowJoin ? "is-on" : ""}`} onClick={() => setD("allowJoin", !draft.allowJoin)} aria-pressed={!!draft.allowJoin} />
                                </div>
                            </section>
                        </div>
                    )}

                    {sec === "design" && (
                        <div className="b-gsm__panel">
                            <section className="b-gsm__section-card">
                                <div className="b-gsm__section-title">Оформление</div>
                                <div className="b-gsm__cover-switch">
                                    <button type="button" className={`b-gsm__cover-switch-btn ${draft.coverMode !== "url" ? "is-active" : ""}`} onClick={() => setD("coverMode", "file")}>Файл</button>
                                    <button type="button" className={`b-gsm__cover-switch-btn ${draft.coverMode === "url" ? "is-active" : ""}`} onClick={() => setD("coverMode", "url")}>Ссылка</button>
                                </div>

                                {draft.coverMode === "url" ? (
                                    <>
                                        <div className="b-gsm__url-wrap">
                                            <GsIcon name="link" size={16} />
                                            <input className="b-input" value={draft.coverUrl || ""} onChange={(e) => setD("coverUrl", e.target.value)} placeholder="https://..." />
                                        </div>
                                        <div className="b-gsm__cover-row">
                                            <div className="b-gsm__cover-preview">
                                                {activeCover ? <img src={activeCover.startsWith("/") ? resolveUrl(activeCover) : activeCover} alt="" /> : null}
                                            </div>
                                            <div className="b-gsm__cover-upload">
                                                <GsIcon name="link" size={24} />
                                                <strong>Ссылка на изображение</strong>
                                                <small>Предпросмотр обновится после вставки URL</small>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="b-gsm__cover-row">
                                        <div className="b-gsm__cover-preview">
                                            {activeCover ? <img src={activeCover.startsWith("/") ? resolveUrl(activeCover) : activeCover} alt="" /> : null}
                                        </div>
                                        <label
                                            className={`b-gsm__cover-upload ${drag ? "is-drag" : ""}`}
                                            onDragEnter={(e) => { e.preventDefault(); setDrag(true); }}
                                            onDragOver={(e) => e.preventDefault()}
                                            onDragLeave={(e) => { e.preventDefault(); setDrag(false); }}
                                            onDrop={onDrop}
                                        >
                                            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => onFileSelected(e.target.files?.[0])} />
                                            <GsIcon name="upload" size={24} />
                                            <strong>{coverUploading ? "Загрузка…" : "Загрузить изображение"}</strong>
                                            <small>JPG, PNG, WebP до 5 МБ</small>
                                        </label>
                                    </div>
                                )}
                                <div className="b-gsm__field-hint" style={{ marginTop: 12 }}>Рекомендуемый размер обложки: 1200×630 px.</div>
                            </section>
                        </div>
                    )}
                </div>

                <div className="b-gsm__foot">
                    <div className="b-gsm__progress">
                        <span>Шаг {activeIndex + 1} из {SECTIONS.length}</span>
                        <span className="b-gsm__dots" aria-hidden="true">
                            {SECTIONS.map((item, idx) => <span key={item.id} className={`b-gsm__dot ${idx === activeIndex ? "is-active" : ""}`} />)}
                        </span>
                    </div>
                    <div className="b-gsm__foot-actions">
                        {activeIndex > 0 ? <button type="button" className="b-gsm__btn" onClick={goPrev} disabled={saving}>Назад</button> : null}
                        <button type="button" className="b-gsm__btn" onClick={onClose} disabled={saving}>Отмена</button>
                        {isLast ? (
                            <button type="button" className="b-gsm__btn b-gsm__btn--primary" onClick={onSave} disabled={!canSave || saving}>
                                {saving ? <><span className="b-gsm__spinner" />Сохранение…</> : (draft.id ? "Сохранить" : "Создать сессию")}
                            </button>
                        ) : (
                            <button type="button" className="b-gsm__btn b-gsm__btn--primary" onClick={goNext} disabled={saving}>Далее</button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── Основной компонент ───────────────────────────────────────────────────────
const MANAGE_ROLES = new Set(["ADMIN", "PSYCHOLOGIST", "MODERATOR", "OWNER", "HOST", "CREATOR"]);

function normalizeRoleCode(role) {
    return String(role || "").trim().toUpperCase().replace(/^ROLE_/, "");
}

function canCreateGroupSessions(role) {
    const code = normalizeRoleCode(role);
    if (!code || code === "CLIENT") return false;
    return MANAGE_ROLES.has(code) || code !== "CLIENT";
}

function joinTitles(arr, limit = 2) {
    return (arr || [])
        .map((x) => {
            if (!x) return null;
            if (typeof x === "string") return x;
            return x?.title || x?.name || x?.label || null;
        })
        .filter(Boolean)
        .slice(0, limit);
}

function pickThemes(row) {
    return row?.themeLabels?.length ? row.themeLabels : (row?.themes || []);
}

function pickMethods(row) {
    return row?.methodLabels?.length ? row.methodLabels : (row?.methods || []);
}

function pickStart(row) {
    return row?.startDateTime || row?.start_datetime || null;
}

function pickEnd(row) {
    return row?.endDateTime || row?.end_datetime || null;
}

function pickCover(row) {
    return row?.coverImg || row?.cover_img || row?.image || row?.coverUrl || null;
}

function pickCapacity(row) {
    return row?.capacityClients ?? row?.capacity_clients ?? row?.capacity ?? null;
}

function pickParticipants(row) {
    return row?.participantsCount ?? row?.clientsCount ?? row?.membersCount ?? (Array.isArray(row?.participants) ? row.participants.length : 0);
}

function pickPrice(row) {
    return row?.priceAtTime ?? row?.price_at_time ?? row?.price ?? null;
}

function isConferenceShadowSession(row) {
    const source = row && typeof row === "object" ? row : {};
    const typeToken = String(
        source?.kind ??
        source?.sessionKind ??
        source?.session_kind ??
        source?.bookingType ??
        source?.booking_type ??
        source?.type ??
        ""
    ).toUpperCase();
    if (typeToken.includes("CONFERENCE") || typeToken.includes("DIRECT")) return true;

    const capacity = Number(source?.capacityClients ?? source?.capacity_clients ?? source?.capacity ?? NaN);
    const minCapacity = Number(source?.minCapacity ?? source?.min_capacity ?? NaN);
    const price = Number(source?.priceAtTime ?? source?.price_at_time ?? source?.price ?? NaN);
    const hasConferenceShape =
        Number.isFinite(capacity) && capacity === 1 &&
        Number.isFinite(minCapacity) && minCapacity === 1 &&
        (!Number.isFinite(price) || price <= 0);
    return hasConferenceShape;
}

function isJoinAllowed(row) {
    const source = row && typeof row === "object" ? row : {};
    const raw = source?.allowJoin ?? source?.allow_join;
    if (raw == null) return true;
    if (typeof raw === "boolean") return raw;
    if (typeof raw === "number") return raw === 1;
    const token = String(raw).trim().toLowerCase();
    if (!token) return true;
    return token === "true" || token === "1" || token === "yes";
}

function pickFormat(row) {
    const raw = String(row?.format || row?.sessionFormat || row?.type || "").toUpperCase();
    if (raw.includes("OFFLINE") || raw.includes("ОФЛ") || row?.offline) return "Офлайн";
    if (row?.location && !row?.telemostUrl && !row?.telemost_url) return "Офлайн";
    return "Онлайн";
}

function formatPrice(value) {
    if (value == null || value === "") return "Бесплатно";
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return "Бесплатно";
    return `${n.toLocaleString("ru-RU")} ₽`;
}

function formatSessionDate(startIso, endIso) {
    try {
        const s = startIso ? new Date(startIso) : null;
        const e = endIso ? new Date(endIso) : null;
        if (!s || Number.isNaN(s.getTime())) return "Дата уточняется";
        const date = s.toLocaleDateString("ru-RU", { day: "2-digit", month: "short" }).replace(".", "");
        const st = `${pad2(s.getHours())}:${pad2(s.getMinutes())}`;
        const et = e && !Number.isNaN(e.getTime()) ? `${pad2(e.getHours())}:${pad2(e.getMinutes())}` : "";
        return et ? `${date} • ${st}–${et}` : `${date} • ${st}`;
    } catch {
        return fmtDateTimeRange(startIso, endIso);
    }
}

function isToday(iso) {
    if (!iso) return false;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return false;
    const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate();
}

function readPaymentUrl(res) {
    return res?.confirmationUrl || res?.confirmation_url || res?.url || res?.payUrl || res?.paymentUrl || null;
}

export default function GroupSessions({ view = "catalog" }) {
    const navigate = useNavigate();
    const location = useLocation();
    const { id: detailRouteIdRaw } = useParams();
    const detailRouteId = detailRouteIdRaw ? String(detailRouteIdRaw) : "";
    const isDetailRoute = Boolean(detailRouteId);
    const { role } = useAuth();
    const toast = useToast();
    const { ensureWithinLimit, limitModal } = useSubscriptionLimitsGate();

    const roleCode = normalizeRoleCode(role);
    const canManageRole = canCreateGroupSessions(roleCode);
    const isMyView = view === "my" && canManageRole;
    const isCatalogView = !isMyView;
    const searchParams = new URLSearchParams(location.search || "");
    const createRequested = searchParams.get("create") === "1";
    const editRequestedId = searchParams.get("edit") || "";

    const [q, setQ] = useState("");
    const [status, setStatus] = useState(view === "my" ? "ALL" : "OPEN");
    const [format, setFormat] = useState("ALL");
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [page, setPage] = useState(0);
    const [hasMore, setHasMore] = useState(false);
    const [joiningId, setJoiningId] = useState(null);

    const [detailOpen, setDetailOpen] = useState(false);
    const [detailLoading] = useState(false);
    const [detail, setDetail] = useState(null);

    const [editOpen, setEditOpen] = useState(false);
    const [allThemes, setAllThemes] = useState([]);
    const [allMethods, setAllMethods] = useState([]);
    const [saving, setSaving] = useState(false);
    const [draft, setDraft] = useState(emptyDraft);

    useEffect(() => {
        dictApi.themes()
            .then(r => setAllThemes(Array.isArray(r) ? r : (r?.content || r?.items || [])))
            .catch(() => {});
        dictApi.methods()
            .then(r => setAllMethods(Array.isArray(r) ? r : (r?.content || r?.items || [])))
            .catch(() => {});
    }, []);

    const load = useCallback(async ({ nextPage = 0, append = false } = {}) => {
        setLoading(true);
        try {
            const r = isMyView
                ? await sessionsApi.listMyGroupSessions({
                    page: nextPage,
                    size: 24,
                    q: q || null,
                    status: status && status !== "ALL" ? status : null,
                })
                : await sessionsApi.listPublicGroupSessions({
                    page: nextPage,
                    size: 24,
                    q: q || null,
                });

            const parsed = pickListResp(r);
            setPage(parsed.page ?? nextPage);
            setHasMore(Boolean(parsed.hasMore));
            setItems(prev => append ? [...prev, ...(parsed.items || [])] : (parsed.items || []));
        } catch (e) {
            console.error(e);
            toast.error(e?.message || "Не удалось загрузить групповые сессии");
        } finally {
            setLoading(false);
        }
    }, [isMyView, q, status, toast]);

    useEffect(() => {
        if (isDetailRoute) return undefined;
        const t = setTimeout(() => load({ nextPage: 0, append: false }), 350);
        return () => clearTimeout(t);
    }, [isDetailRoute, load]);
    useEffect(() => {
        if (isDetailRoute) return;
        if (isMyView && createRequested && !editOpen) {
            setDraft(emptyDraft());
            setEditOpen(true);
        }
    }, [isDetailRoute, isMyView, createRequested, editOpen]);

    useEffect(() => {
        setStatus(isMyView ? "ALL" : "OPEN");
    }, [isMyView]);


    const openCreate = () => {
        if (!isMyView) {
            toast.info(canManageRole ? "Создание доступно в разделе «Мои сессии»" : "Клиенты могут смотреть и записываться на сессии, но не создавать их");
            return;
        }
        setDraft(emptyDraft());
        setEditOpen(true);
    };

    const openEdit = useCallback(async (id) => {
        if (!isMyView) return;
        if (!id) return;
        setSaving(false);
        setDraft(d => ({ ...d, id }));
        setEditOpen(true);
        try {
            const d = await sessionsApi.getMyGroupSession(id);
            const start = isoToDateAndTime(d?.startDateTime || d?.start_datetime);
            const end   = isoToDateAndTime(d?.endDateTime   || d?.end_datetime);
            const existingCover = d?.coverImg || d?.cover_img || "";

            setDraft({
                id: d?.id ?? id,
                title: d?.title || "",
                cover: existingCover.startsWith("http") ? "" : existingCover,
                coverUrl: existingCover.startsWith("http") ? existingCover : "",
                coverMode: existingCover.startsWith("http") ? "url" : "file",
                coverImg: existingCover,
                sessionFormat: pickFormat(d) === "Офлайн" ? "OFFLINE" : "ONLINE",
                description: d?.description || "",
                date: start.date,
                timeStart: start.time,
                timeEnd: end.time,
                priceAtTime: d?.priceAtTime ?? d?.price_at_time ?? "",
                capacityClients: d?.capacityClients ?? d?.capacity_clients ?? "",
                minCapacity: d?.minCapacity ?? d?.min_capacity ?? "",
                allowJoin: Boolean(d?.allowJoin ?? d?.allow_join ?? true),
                themeIds: Array.isArray(d?.themeIds) ? d.themeIds : [],
                methodIds: Array.isArray(d?.methodIds) ? d.methodIds : [],
            });
        } catch (e) {
            toast.error(e?.message || "Не удалось загрузить сессию");
        }
    }, [isMyView, toast]);

    const openDetail = async (id) => {
        if (!id) return;
        navigate(isMyView ? `/dashboard/group-sessions/my/${encodeURIComponent(String(id))}` : `/dashboard/group-sessions/${encodeURIComponent(String(id))}`);
    };

    useEffect(() => {
        if (isDetailRoute) return;
        if (isMyView && editRequestedId && !editOpen) {
            openEdit(editRequestedId);
        }
    }, [isDetailRoute, isMyView, editRequestedId, editOpen, openEdit]);

    const closeSession = async (row) => {
        if (!isMyView) return;
        if (!row?.id) return;
        const cnt = Number(pickParticipants(row));
        if (cnt > 0) { toast.info("Нельзя закрыть сессию: уже есть записи"); return; }
        // eslint-disable-next-line no-restricted-globals
        if (!confirm("Закрыть сессию? Она станет недоступна для записи.")) return;
        try {
            await sessionsApi.closeMyGroupSession(row.id);
            toast.success("Сессия закрыта");
            await load({ nextPage: 0, append: false });
        } catch (e) {
            toast.error(e?.message || "Не удалось закрыть сессию");
        }
    };

    const joinSession = async (row) => {
        const id = row?.id;
        if (!id || joiningId) return;
        setJoiningId(id);
        try {
            await sessionsApi.joinGroupSession(id);
            const price = Number(pickPrice(row));
            if (Number.isFinite(price) && price > 0) {
                const pay = await sessionsApi.startGroupPayment(id, {});
                const url = readPaymentUrl(pay);
                if (url) {
                    toast.success("Запись создана, переходим к оплате…");
                    assignExternalUrl(url);
                    return;
                }
                toast.info("Запись создана. Ссылка на оплату временно недоступна.");
            } else {
                toast.success("Вы записались на групповую сессию");
            }
            await load({ nextPage: 0, append: false });
        } catch (e) {
            toast.error(e?.message || "Не удалось записаться на сессию");
        } finally {
            setJoiningId(null);
        }
    };

    const saveDraft = async () => {
        const finalCover = draft.coverMode === "url"
            ? (draft.coverUrl?.trim() || null)
            : (draft.cover?.trim() || null);

        const startIso = dateAndTimeToIso(draft.date, draft.timeStart);
        const endIso   = draft.timeEnd ? dateAndTimeToIso(draft.date, draft.timeEnd) : null;

        if (!startIso) { toast.error("Некорректная дата/время начала"); return; }

        if (!draft.id) {
            try {
                const sessionsRaw = await sessionsApi.listMyGroupSessions({ page: 0, size: 200 });
                const parsed = pickListResp(sessionsRaw);
                const usage = (parsed.items || []).filter((row) => !isConferenceShadowSession(row)).length;
                const allowed = ensureWithinLimit({ featureKey: "groupSessions", usage });
                if (!allowed) return;
            } catch (limitError) {
                toast.error(limitError?.message || "Не удалось проверить лимит подписки");
                return;
            }
        }

        setSaving(true);
        try {
            const payload = {
                title: draft.title?.trim(),
                coverImg: finalCover,
                description: draft.description?.trim() || null,
                startDateTime: startIso,
                endDateTime: endIso,
                sessionFormat: draft.sessionFormat || "ONLINE",
                format: draft.sessionFormat || "ONLINE",
                priceAtTime: draft.priceAtTime === "" ? null : Number(draft.priceAtTime),
                capacityClients: draft.capacityClients === "" ? null : Number(draft.capacityClients),
                minCapacity: draft.minCapacity === "" ? null : Number(draft.minCapacity),
                allowJoin: Boolean(draft.allowJoin),
                themeIds: Array.isArray(draft.themeIds) ? draft.themeIds : [],
                methodIds: Array.isArray(draft.methodIds) ? draft.methodIds : [],
            };

            if (draft.id) {
                await sessionsApi.updateMyGroupSession(draft.id, payload);
                toast.success("Сессия обновлена");
            } else {
                await sessionsApi.createMyGroupSession(payload);
                toast.success("Сессия создана");
            }

            setEditOpen(false);
            setDraft(emptyDraft());
            if (createRequested || editRequestedId) navigate("/dashboard/group-sessions/my", { replace: true });
            await load({ nextPage: 0, append: false });
        } catch (e) {
            console.error(e);
            toast.error(e?.message || "Не удалось сохранить");
        } finally {
            setSaving(false);
        }
    };

    const closeEdit = () => {
        if (saving) return;
        setEditOpen(false);
        setDraft(emptyDraft());
        if (createRequested || editRequestedId) navigate("/dashboard/group-sessions/my", { replace: true });
    };

    const sortedItems = useMemo(() => {
        const arr = Array.isArray(items) ? items.slice() : [];
        arr.sort((a, b) => String(pickStart(a) || "").localeCompare(String(pickStart(b) || "")));
        return arr.filter((row) => {
            if (isCatalogView && (!isJoinAllowed(row) || isConferenceShadowSession(row))) return false;
            if (status !== "ALL" && isCatalogView && toCode(row?.status) !== status) return false;
            if (format !== "ALL" && pickFormat(row).toUpperCase() !== format) return false;
            return true;
        });
    }, [items, status, format, isCatalogView]);

    const stats = useMemo(() => {
        const active = sortedItems.filter(x => ["", "OPEN", "PAID"].includes(toCode(x?.status))).length;
        const participants = sortedItems.reduce((sum, x) => sum + Number(pickParticipants(x) || 0), 0);
        const todayOnline = sortedItems.filter(x => isToday(pickStart(x)) && pickFormat(x) === "Онлайн").length;
        return { active, participants, todayOnline };
    }, [sortedItems]);

    const renderSessionActions = (row) => {
        const id = row?.id;
        const participants = Number(pickParticipants(row));
        const canClose = participants === 0;
        const cancelled = toCode(row?.status) === "CANCELLED";

        if (isMyView) {
            return (
                <div className="b-gs-card__actions b-gs-card__actions--manage">
                    <button type="button" className="b-gs-card__btn" onClick={() => openDetail(id)}>Детали</button>
                    <button type="button" className="b-gs-card__btn" onClick={() => openEdit(id)}>Изменить</button>
                    <button
                        type="button"
                        className="b-gs-card__btn b-gs-card__btn--dark"
                        onClick={() => closeSession(row)}
                        disabled={!canClose || cancelled}
                        title={!canClose ? "Нельзя закрыть: есть записи" : "Закрыть"}
                    >
                        Закрыть
                    </button>
                </div>
            );
        }

        return (
            <div className="b-gs-card__actions">
                <button type="button" className="b-gs-card__btn" onClick={() => openDetail(id)}>Детали</button>
                <button
                    type="button"
                    className="b-gs-card__btn b-gs-card__btn--dark"
                    onClick={() => joinSession(row)}
                    disabled={joiningId === id || toCode(row?.status) === "CANCELLED" || !isJoinAllowed(row)}
                >
                    {joiningId === id ? "Запись…" : "Записаться"}
                </button>
            </div>
        );
    };

    if (isDetailRoute) {
        return (
            <GroupSessionDetailPage
                sessionId={detailRouteId}
                isMyView={isMyView}
                onBack={() => navigate(isMyView ? "/dashboard/group-sessions/my" : "/dashboard/group-sessions")}
                onEdit={(id) => navigate(`/dashboard/group-sessions/my?edit=${encodeURIComponent(String(id))}`)}
                onCloseSession={(row) => closeSession(row)}
            />
        );
    }

    return (
        <div className="b-group-sessions">
            <div className="b-group-sessions__topline">
                <div className="b-group-sessions__intro">
                    <h2>{isMyView ? "Мои сессии" : "Групповые сессии"}</h2>
                    <p>
                        {isMyView
                            ? "Создавайте и управляйте своими групповыми встречами. Закрыть сессию можно только если на неё никто не записался."
                            : "Находите и присоединяйтесь к групповым встречам. Управление и создание вынесены в отдельный раздел «Мои сессии»."}
                    </p>
                    <div className="b-group-sessions__badges" aria-label="Правила доступа">
                        <span className="b-gs-access b-gs-access--green"><GsIcon name={isMyView ? "folder" : "users"} size={16} />{isMyView ? "Мой слой управления" : "Доступно всем"}</span>
                        <span className="b-gs-access b-gs-access--purple"><GsIcon name="shield" size={16} />{isMyView ? "Создание и управление" : "Запись на открытые сессии"}</span>
                    </div>
                </div>

                <div className="b-group-sessions__cta">
                    {isMyView ? (
                        <button type="button" className="b-group-sessions__create" onClick={openCreate}>
                            <span>+</span> Создать сессию
                        </button>
                    ) : canManageRole ? (
                        <div className="b-group-sessions__client-note">
                            <span aria-hidden="true"><GsIcon name="arrow" size={17} /></span>
                            Управление и создание доступны в разделе «Мои сессии».
                            <button type="button" className="b-btn b-btn--transparent" onClick={() => navigate("/dashboard/group-sessions/my")}>
                                Перейти
                            </button>
                        </div>
                    ) : (
                        <div className="b-group-sessions__client-note">
                            <span aria-hidden="true"><GsIcon name="lock" size={17} /></span>
                            Вы можете смотреть и записываться на открытые сессии.
                        </div>
                    )}
                </div>
            </div>

            <div className="b-group-sessions__layout">
                <main className="b-group-sessions__main">
                    <div className="b-group-sessions__filtersPanel">
                        <label className="b-group-sessions__search">
                            <span aria-hidden="true"><GsIcon name="search" size={18} /></span>
                            <input
                                value={q}
                                onChange={e => setQ(e.target.value)}
                                placeholder="Поиск по названию или теме"
                            />
                        </label>
                        <label className="b-group-sessions__select">
                            <span>Статус</span>
                            <select value={status} onChange={e => setStatus(e.target.value)}>
                                <option value="OPEN">Открытые</option>
                                <option value="ALL">Все</option>
                                {isMyView && <option value="CANCELLED">Закрытые</option>}
                                {isMyView && <option value="COMPLETED">Завершённые</option>}
                            </select>
                        </label>
                        <label className="b-group-sessions__select">
                            <span>Формат</span>
                            <select value={format} onChange={e => setFormat(e.target.value)}>
                                <option value="ALL">Все</option>
                                <option value="ОНЛАЙН">Онлайн</option>
                                <option value="ОФЛАЙН">Офлайн</option>
                            </select>
                        </label>
                        <button
                            type="button"
                            className="b-group-sessions__refresh"
                            onClick={() => load({ nextPage: 0, append: false })}
                            disabled={loading}
                            title="Обновить"
                        >
                            {loading ? "…" : <GsIcon name="refresh" size={18} />}
                        </button>
                    </div>

                    <div className="b-group-sessions__stats">
                        <div className="b-gs-stat">
                            <span className="b-gs-stat__icon b-gs-stat__icon--green"><GsIcon name="pulse" size={23} /></span>
                            <span className="b-gs-stat__meta"><b>{stats.active}</b><small>Активные сессии</small></span>
                        </div>
                        <div className="b-gs-stat">
                            <span className="b-gs-stat__icon b-gs-stat__icon--purple"><GsIcon name="users" size={23} /></span>
                            <span className="b-gs-stat__meta"><b>{stats.participants}</b><small>Участники</small></span>
                        </div>
                        <div className="b-gs-stat">
                            <span className="b-gs-stat__icon b-gs-stat__icon--blue"><GsIcon name="wifi" size={23} /></span>
                            <span className="b-gs-stat__meta"><b>{stats.todayOnline}</b><small>Онлайн сегодня</small></span>
                        </div>
                    </div>

                    {loading && !sortedItems.length ? (
                        <div className="b-group-sessions__empty b-group-sessions__empty--loading">
                            <div className="b-group-sessions__empty-ico"><GsIcon name="refresh" size={24} /></div>
                            <div className="b-group-sessions__empty-title">Загружаем групповые сессии…</div>
                        </div>
                    ) : sortedItems.length ? (
                        <div className="b-group-sessions__grid">
                            {sortedItems.map((row, idx) => {
                                const id = row?.id;
                                const title = row?.title || "Групповая сессия";
                                const cover = pickCover(row);
                                const coverUrl = cover ? resolveUrl(cover) : "";
                                const startIso = pickStart(row);
                                const endIso = pickEnd(row);
                                const cap = pickCapacity(row);
                                const cnt = pickParticipants(row);
                                const price = pickPrice(row);
                                const chips = [...joinTitles(pickThemes(row), 2), ...joinTitles(pickMethods(row), 2)].slice(0, 2);
                                const formatText = pickFormat(row);

                                return (
                                    <article key={id || `${title}_${idx}`} className="b-gs-card">
                                        <button
                                            type="button"
                                            className="b-gs-card__cover"
                                            onClick={() => openDetail(id)}
                                            title="Открыть детали"
                                        >
                                            {coverUrl
                                                ? <img loading="lazy" decoding="async" src={coverUrl} alt="" />
                                                : <div className={`b-gs-card__cover-ph b-gs-card__cover-ph--${(idx % 6) + 1}`} />
                                            }
                                            <span className={`b-gs-status b-gs-status--${statusKind(row?.status || "OPEN")}`}>
                                                {humanStatus(row?.status || "OPEN")}
                                            </span>
                                        </button>

                                        <div className="b-gs-card__body">
                                            <h3 title={title}>{title}</h3>
                                            <div className="b-gs-card__chips">
                                                {(chips.length ? chips : ["Поддержка", "Группа"]).map((chip) => (
                                                    <span key={chip}>{chip}</span>
                                                ))}
                                            </div>
                                            <div className="b-gs-card__facts">
                                                <span><GsIcon name="calendar" size={15} /> {formatSessionDate(startIso, endIso)}</span>
                                                <span><GsIcon name={formatText === "Онлайн" ? "monitor" : "pin"} size={15} /> {formatText}</span>
                                                <span><GsIcon name="users" size={15} /> {cap ? `${cnt} / ${cap}` : cnt} участников</span>
                                                <span className="b-gs-card__price"><GsIcon name="tag" size={15} /> {formatPrice(price)}</span>
                                            </div>
                                            {renderSessionActions(row)}
                                        </div>
                                    </article>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="b-group-sessions__empty">
                            <div className="b-group-sessions__empty-ico"><GsIcon name="leaf" size={24} /></div>
                            <div className="b-group-sessions__empty-title">Пока нет групповых сессий</div>
                            <p>
                                {isMyView
                                    ? "Создайте первую встречу, чтобы участники могли записаться."
                                    : "Когда специалисты откроют набор в группы, они появятся здесь."}
                            </p>
                            {isMyView && <button type="button" className="b-btn" onClick={openCreate}>Создать первую</button>}
                        </div>
                    )}

                    {hasMore && (
                        <div className="b-group-sessions__more">
                            <button
                                type="button"
                                className="b-btn b-btn--transparent"
                                onClick={() => load({ nextPage: page + 1, append: true })}
                            >
                                Показать ещё
                            </button>
                        </div>
                    )}
                </main>

                <aside className="b-group-sessions__aside">
                    <section className="b-group-sessions__infoCard">
                        <div className="b-group-sessions__infoIcon"><GsIcon name={isMyView ? "folder" : "lock"} size={18} /></div>
                        <h3>{isMyView ? "Мои сессии" : "Доступ и запись"}</h3>
                        <p>{isMyView ? "Здесь собраны только ваши групповые сессии и инструменты управления ими." : "В этом разделе отображаются групповые сессии, на которые можно записаться."}</p>
                        <ul>
                            {isMyView ? (
                                <>
                                    <li><span><GsIcon name="spark" size={17} /></span> Создавайте новые сессии в один клик.</li>
                                    <li><span><GsIcon name="note" size={17} /></span> Редактируйте параметры, описание и стоимость.</li>
                                    <li><span><GsIcon name="check" size={17} /></span> Закрыть сессию можно только без записей.</li>
                                </>
                            ) : (
                                <>
                                    <li><span><GsIcon name="eye" size={17} /></span> Открывайте детали и проверяйте условия участия.</li>
                                    <li><span><GsIcon name="calendar" size={17} /></span> Записывайтесь только на открытые сессии.</li>
                                    <li><span><GsIcon name="arrow" size={17} /></span> Управление и создание вынесены в раздел «Мои сессии».</li>
                                </>
                            )}
                        </ul>
                    </section>
                    <section className="b-group-sessions__helpCard">
                        <h3>{isMyView ? "Подсказка" : "Нужна помощь?"}</h3>
                        <p>{isMyView ? "Используйте детали сессии, чтобы проверить участников, дату, цену и описание перед изменением или закрытием." : "Используйте детали сессии, чтобы посмотреть дату, формат, цену и описание перед записью."}</p>
                    </section>
                </aside>
            </div>

            <Modal
                open={detailOpen}
                title={detail?.title || "Детали групповой сессии"}
                onClose={() => { setDetailOpen(false); setDetail(null); }}
                actions={detail?.id ? (
                    isMyView ? (
                        <>
                            <button type="button" className="b-btn b-btn--transparent" onClick={() => openEdit(detail.id)}>
                                Редактировать
                            </button>
                            <button
                                type="button"
                                className="b-btn"
                                onClick={() => closeSession({
                                    id: detail.id,
                                    clientsCount: pickParticipants(detail),
                                    status: detail?.status,
                                })}
                                disabled={Number(pickParticipants(detail)) > 0 || toCode(detail?.status) === "CANCELLED"}
                            >
                                Закрыть
                            </button>
                        </>
                    ) : (
                        <button
                            type="button"
                            className="b-btn"
                            onClick={() => joinSession(detail)}
                            disabled={joiningId === detail.id || toCode(detail?.status) === "CANCELLED" || !isJoinAllowed(detail)}
                        >
                            {joiningId === detail.id ? "Запись…" : "Записаться"}
                        </button>
                    )
                ) : null}
            >
                <div className="b-gs-modalScroll">
                    {detailLoading ? (
                        <div className="b-group-sessions__detail-loading">Загрузка…</div>
                    ) : detail ? (
                        <div className="b-gs-detail">
                            <div className="b-gs-detail__top">
                                <div className="b-gs-detail__cover">
                                    {pickCover(detail) ? (
                                        <img loading="lazy" decoding="async" src={resolveUrl(pickCover(detail))} alt="" />
                                    ) : (
                                        <div className="b-gs-detail__cover-ph" />
                                    )}
                                </div>
                                <div className="b-gs-detail__info">
                                    <div className={`b-gs-status b-gs-status--${statusKind(detail?.status || "OPEN")}`}>
                                        {humanStatus(detail?.status || "OPEN")}
                                    </div>
                                    <div className="b-gs-detail__kv">
                                        <div className="b-gs-detail__k">Дата</div>
                                        <div className="b-gs-detail__v">{fmtDateTimeRange(pickStart(detail), pickEnd(detail))}</div>
                                        <div className="b-gs-detail__k">Формат</div>
                                        <div className="b-gs-detail__v">{pickFormat(detail)}</div>
                                        <div className="b-gs-detail__k">Цена</div>
                                        <div className="b-gs-detail__v">{formatPrice(pickPrice(detail))}</div>
                                        <div className="b-gs-detail__k">Лимит</div>
                                        <div className="b-gs-detail__v">
                                            {pickCapacity(detail) ? `${pickParticipants(detail)}/${pickCapacity(detail)}` : "без лимита"}
                                        </div>
                                        {detail?.minCapacity != null && (
                                            <>
                                                <div className="b-gs-detail__k">Мин. участников</div>
                                                <div className="b-gs-detail__v">{detail.minCapacity}</div>
                                            </>
                                        )}
                                        <div className="b-gs-detail__k">Дозапись</div>
                                        <div className="b-gs-detail__v">{detail?.allowJoin ?? detail?.allow_join ?? true ? "разрешена" : "закрыта"}</div>
                                        {(pickThemes(detail)?.length > 0) && (
                                            <>
                                                <div className="b-gs-detail__k">Темы</div>
                                                <div className="b-gs-detail__v">{joinTitles(pickThemes(detail), 10).join(", ")}</div>
                                            </>
                                        )}
                                        {(pickMethods(detail)?.length > 0) && (
                                            <>
                                                <div className="b-gs-detail__k">Методы</div>
                                                <div className="b-gs-detail__v">{joinTitles(pickMethods(detail), 10).join(", ")}</div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>
                            {detail?.description && (
                                <div className="b-gs-detail__desc">
                                    <div className="b-gs-detail__desc-title">Описание</div>
                                    <div className="b-gs-detail__desc-text">{detail.description}</div>
                                </div>
                            )}
                            {detail?.minCapacity != null && (
                                <div className="b-gs-detail__warn">
                                    <GsIcon name="info" size={17} /> Если к началу сессии не наберётся минимум <b>{detail.minCapacity}</b> участников, сессия будет автоматически отменена, а оплата возвращена.
                                </div>
                            )}
                            {isMyView && (
                                <div className="b-gs-detail__participants">
                                    <div className="b-gs-detail__participants-head">
                                        <div className="b-gs-detail__participants-title">
                                            Участники ({(detail?.participants || []).length})
                                        </div>
                                    </div>
                                    {(detail?.participants || []).length ? (
                                        <div className="b-gs-participants">
                                            {detail.participants.map((p, idx) => {
                                                const user = p?.user || p;
                                                const avatar = user?.avatarUrl || user?.avatar_url || null;
                                                const payText = p?.paymentStatus ? toCode(p.paymentStatus) : "";
                                                const stText  = p?.status ? toCode(p.status) : "";
                                                return (
                                                    <div
                                                        key={p?.id || user?.id || `${user?.email || "u"}_${idx}`}
                                                        className="b-gs-participants__item"
                                                    >
                                                        <div className="b-gs-participants__avatar">
                                                            {avatar
                                                                ? <img loading="lazy" decoding="async" src={resolveUrl(avatar)} alt="" />
                                                                : <div className="b-gs-participants__avatar-ph" />
                                                            }
                                                        </div>
                                                        <div className="b-gs-participants__meta">
                                                            <div className="b-gs-participants__name">{participantLabel(user)}</div>
                                                            <div className="b-gs-participants__sub">
                                                                {payText ? `Оплата: ${payText}` : null}
                                                                {stText ? (payText ? ` · Статус: ${stText}` : `Статус: ${stText}`) : null}
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="b-gs-detail__participants-empty">Пока никто не записался</div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="b-group-sessions__detail-empty">Не удалось загрузить</div>
                    )}
                </div>
            </Modal>

            {isMyView && (
                <EditModal
                    open={editOpen}
                    draft={draft}
                    setDraft={setDraft}
                    saving={saving}
                    allThemes={allThemes}
                    allMethods={allMethods}
                    onClose={closeEdit}
                    onSave={saveDraft}
                />
            )}
            {limitModal}
        </div>
    );
}
